// components/ScraperDashboard/LiveLogs.js - Real-time log display component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Terminal, Wifi, WifiOff, ChevronDown, Filter,
  Trash2, AlertCircle, Info, AlertTriangle, Bug, Copy, Check
} from 'lucide-react';
import './LiveLogs.css';

const LEVEL_CONFIG = {
  debug: { icon: Bug, className: 'log-debug', label: 'Debug' },
  info: { icon: Info, className: 'log-info', label: 'Info' },
  warn: { icon: AlertTriangle, className: 'log-warn', label: 'Warn' },
  error: { icon: AlertCircle, className: 'log-error', label: 'Error' }
};

const LEVEL_FILTERS = ['all', 'debug', 'info', 'warn', 'error'];

function LiveLogs({ logs, connected, error, usingPolling, onClear, getFilteredLogs }) {
  const [levelFilter, setLevelFilter] = useState('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [copied, setCopied] = useState(false);

  const listRef = useRef(null);
  const wasAtBottomRef = useRef(true);

  // Copy all visible logs to clipboard
  const handleCopyAll = () => {
    const logsToShow = levelFilter === 'all' ? logs : getFilteredLogs({ level: levelFilter });
    const lines = logsToShow.map(log => {
      const ts = new Date(log.timestamp).toISOString();
      const level = (log.level || 'info').toUpperCase().padEnd(5);
      const ctx = log.context;
      const prefix = ctx?.operation ? `[${ctx.operation.toUpperCase()}]` : '';
      const stage = ctx?.stage ? `[${ctx.stage}]` : '';
      const jobId = ctx?.jobId ? `#${ctx.jobId}` : '';
      return `${ts} ${level} ${prefix}${stage}${jobId} ${log.message}`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Filter logs based on level
  const filteredLogs = levelFilter === 'all'
    ? logs
    : getFilteredLogs({ level: levelFilter });

  // Auto-scroll handling
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    wasAtBottomRef.current = atBottom;
    setAutoScroll(atBottom);
  }, []);

  // Scroll to bottom when new logs arrive and auto-scroll is enabled
  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = 0; // Logs are newest first
    }
  }, [logs, autoScroll]);

  const scrollToLatest = () => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
      setAutoScroll(true);
    }
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const formatContext = (context) => {
    if (!context) return null;
    const parts = [];
    if (context.operation) parts.push(context.operation.toUpperCase());
    if (context.stage) parts.push(context.stage);
    if (context.jobId) parts.push(`#${context.jobId}`);
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  const getLevelCounts = () => {
    const counts = { debug: 0, info: 0, warn: 0, error: 0 };
    logs.forEach(l => {
      if (counts[l.level] !== undefined) counts[l.level]++;
    });
    return counts;
  };

  const levelCounts = getLevelCounts();

  return (
    <div className={`ll-container ${expanded ? 'll-expanded' : 'll-collapsed'}`}>
      {/* Header */}
      <div className="ll-header" onClick={() => setExpanded(!expanded)}>
        <div className="ll-header-left">
          <Terminal size={16} />
          <span className="ll-title">Live Logs</span>
          <span className={`ll-connection ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? <Wifi size={12} /> : <WifiOff size={12} />}
            {connected ? (usingPolling ? 'Polling' : 'Live') : 'Offline'}
          </span>
          {error && <span className="ll-error-badge">{error}</span>}
        </div>
        <div className="ll-header-right">
          <span className="ll-count">{logs.length} entries</span>
          <ChevronDown
            size={16}
            className={`ll-chevron ${expanded ? 'rotated' : ''}`}
          />
        </div>
      </div>

      {expanded && (
        <>
          {/* Toolbar */}
          <div className="ll-toolbar">
            <div className="ll-filters">
              <Filter size={12} />
              {LEVEL_FILTERS.map(level => (
                <button
                  key={level}
                  className={`ll-filter-btn ${levelFilter === level ? 'active' : ''} ${level !== 'all' ? `ll-filter-${level}` : ''}`}
                  onClick={(e) => { e.stopPropagation(); setLevelFilter(level); }}
                >
                  {level === 'all' ? 'All' : (
                    <>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                      <span className="ll-filter-count">{levelCounts[level]}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
            <div className="ll-actions">
              {!autoScroll && (
                <button className="ll-action-btn ll-jump-btn" onClick={scrollToLatest}>
                  Jump to Latest
                </button>
              )}
              <button
                className={`ll-action-btn ${copied ? 'll-copied' : ''}`}
                onClick={handleCopyAll}
                title="Copy all logs"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button className="ll-action-btn" onClick={onClear} title="Clear logs">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Log List */}
          <div
            className="ll-list"
            ref={listRef}
            onScroll={handleScroll}
          >
            {filteredLogs.length === 0 ? (
              <div className="ll-empty">
                <Terminal size={24} />
                <span>No logs yet. Trigger a scrape to see live output.</span>
              </div>
            ) : (
              filteredLogs.map(log => {
                const config = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
                const Icon = config.icon;
                const contextStr = formatContext(log.context);

                return (
                  <div
                    key={log.id}
                    className={`ll-entry ${config.className} ${selectedLog?.id === log.id ? 'selected' : ''}`}
                    onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                  >
                    <div className="ll-entry-main">
                      <Icon size={12} className="ll-level-icon" />
                      <span className="ll-timestamp">{formatTimestamp(log.timestamp)}</span>
                      {contextStr && <span className="ll-context">{contextStr}</span>}
                      <span className="ll-message">{log.message}</span>
                    </div>
                    {selectedLog?.id === log.id && log.context && (
                      <div className="ll-entry-details">
                        <pre>{JSON.stringify(log.context, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default LiveLogs;
